<script setup>
import PageContainer from "../components/PageContainer";
//Stage 1: Component that accepts Email address to identify user
import EmailForm from "./LoginStages/EmailForm";
//Stage 2: Component that accepts Email OTP and sends Auth Code E-Mail
import EmailOTPForm from "./LoginStages/EmailOTPForm";
//Stage 3: Display Option to setup or enter MFA
import MFAChoice from "./LoginStages/MFAChoice";
//Stage 4a: Setup Authenticator MFA
import AuthenticatorSetupForm from "./LoginStages/AuthenticatorSetupForm";
//Stage 5a: Component that accepts Authenticator OTP
import AuthenticatorOTPForm from "./LoginStages/AuthenticatorOTPForm";
//Stage 4b: Setup SMS MFA
import SMSSetupForm from "./LoginStages/SMSSetupForm";
//Stage 5b: Component that accepts SMS OTP anbd sends Auth Code SMS
import SMSOTPForm from "./LoginStages/SMSOTPForm";

import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from 'vue-router';
import { reactive, ref, computed, onMounted, inject } from 'vue';
import axios from "axios";
import { to } from "await-to-js";
import { get } from "lodash";
import { useEnvStore } from "@dutypay/front-end-design-system";
import { buildRedirectUrl } from "../utils/redirect";

const { t } = useI18n();
const { locale } = useI18n({useScope: 'global'})
const router = useRouter();
const route = useRoute();
const currentComponent = ref('');
const component = {
  PageContainer,
  EmailForm,
  EmailOTPForm,
  MFAChoice,
  AuthenticatorSetupForm,
  AuthenticatorOTPForm,
  SMSSetupForm,
  SMSOTPForm
}

const COMPONENTS = {
  EMAIL_FORM: "EmailForm",
  EMAIL_OTP_FORM: "EmailOTPForm",
  MFA_CHOICE: "MFAChoice",
  AUTHENTICATOR_SETUP_FORM: "AuthenticatorSetupForm",
  AUTHENTICATOR_OTP_FORM: "AuthenticatorOTPForm",
  SMS_SETUP_FORM: "SMSSetupForm",
  SMS_OTP_FORM: "SMSOTPForm",
  ENTER_AUTHENTICATOR_CODE: "EnterAuthenticatorCode",
  ENTER_SMS_CODE: "EnterSMSCode"
};

const loginData = reactive({
  email: "",
});
const notification = reactive({
  authenticate: {
    asyncError: false
  }
})
const {hideLogoutNotification, hasLogoutNotification} = inject('logoutNotification')
const { accountActivationNotification } = inject('accountActivationNotification')
function resetNotifications() {
  notification.authenticate.asyncError = false;
  hideLogoutNotification();
}
const envStore = useEnvStore();

const hasAuthenticatorMFA = computed(() => {
  return get(loginData, "mfaModes.authenticator", false);
});
const hasSMSMFA = computed(() => {
  return get(loginData, "mfaModes.sms", false);
});
const hasNoMFA = computed(() => {
  return !hasAuthenticatorMFA.value && !hasSMSMFA.value;
});
const hasAtLeastOneMFA = computed(() => {
  return !hasNoMFA.value;
});
const isSetupComponent = computed(() => {
  return (
    currentComponent.value === COMPONENTS.AUTHENTICATOR_SETUP_FORM
    || currentComponent.value === COMPONENTS.SMS_SETUP_FORM
  );
});
const isEnterComponent = computed(() => {
  return (
    currentComponent.value === COMPONENTS.AUTHENTICATOR_OTP_FORM
    || currentComponent.value === COMPONENTS.SMS_OTP_FORM
  );
});
const isEmailOTPComponent = computed(() => {
  return currentComponent.value === COMPONENTS.EMAIL_OTP_FORM;
});
const isMFAChoiceComponent = computed(() => {
  return currentComponent.value === COMPONENTS.MFA_CHOICE;
})

function resetLoginData() {
  resetNotifications();
  goToEmailForm();
  loginData.email = "";
}

function goToEmailForm() {
  setCurrentComponent(COMPONENTS.EMAIL_FORM);
}

function goToMFA() {
  if (hasAuthenticatorMFA.value && !hasSMSMFA.value) {
    setCurrentComponent(COMPONENTS.ENTER_AUTHENTICATOR_CODE);
  } else if (hasSMSMFA.value && !hasAuthenticatorMFA.value) {
    setCurrentComponent(COMPONENTS.ENTER_SMS_CODE);
  } else {
    setCurrentComponent(COMPONENTS.MFA_CHOICE);
  }
}
function setCurrentComponent(component) {
  resetNotifications();
  currentComponent.value = component;
}
function skipSetup() {
  delete loginData.totp;
  delete loginData.firstTime;
  delete loginData.mode;
  triggerAuthenticate();
}
async function triggerAuthenticate() {
  envStore.setGlobalLoading();
  resetNotifications();
  let err, tokens;
  [err, tokens] = await to(
    axios({
      url: `${ envStore.apiUrls.dutypay }/api/v0/authenticate`,
      data: loginData,
      method: "POST",
    })
  );
  if (err) {
    envStore.releaseGlobalLoading();
    notification.authenticate.asyncError = true;
    return;
  }
  tokens = tokens.data.result;
  localStorage.setItem("accessToken", tokens.accessToken);
  localStorage.setItem("refreshToken", tokens.refreshToken);
  let i18n = locale.value;
  envStore.releaseGlobalLoading();
  if (route.query.redirect) {
    window.location.href = buildRedirectUrl(route.query.redirect, {
      i18n,
      token: tokens.oneTimeToken,
    });
    return;
  }
  // TODO: Add host URL to .env file
  window.location.href = `https://${ envStore.isDevelopment ? 'dev-' : '' }app.contractshub.eu/#i18n=${ i18n }&token=${ tokens.oneTimeToken }`;
}

onMounted(async() => {
  await router.isReady();
  let queryParams = route.query;
  //Check for emailAuthCode and email in URL Query Params
  if (queryParams.emailAuthCode && queryParams.email) {
    setCurrentComponent(COMPONENTS.EMAIL_OTP_FORM);
    return;
  }
  setCurrentComponent(COMPONENTS.EMAIL_FORM);
})
</script>
<script>
export default {
  name: "LoginStages"
};
</script>

<template>
  <PageContainer>
    <DpNotification
      type="success"
      :visibility="accountActivationNotification.success"
      :title="t('sso.pages.LoginStages.notification.accountActivation.success.title')"
      :message="t('sso.pages.LoginStages.notification.accountActivation.success.message')"/>
    
    <DpNotification
      type="error"
      :visibility="accountActivationNotification.error"
      :title="t('sso.pages.LoginStages.notification.accountActivation.asyncError.title')"
      :message="t('sso.pages.LoginStages.notification.accountActivation.asyncError.message')"/>

    <DpNotification
      type="success"
      :visibility="hasLogoutNotification"
      :title="t('sso.pages.LoginStages.notification.logout.success.title')"
      :message="t('sso.pages.LoginStages.notification.logout.success.message')"/>

    <DpNotification
      type="error"
      :visibility="notification.authenticate.asyncError"
      :title="t('sso.pages.LoginStages.notification.authenticate.asyncError.title')"
      :message="t('sso.pages.LoginStages.notification.authenticate.asyncError.message')"/>

    <component
      v-bind:is="component[currentComponent]"
      v-model="loginData"
      v-on:reset-login-data="resetLoginData"
      v-on:go-to="setCurrentComponent($event)"
      v-on:trigger-authenticate="triggerAuthenticate"
      v-on:fetched-mfa-modes="goToMFA"
      v-on:login-form-set="loginData = $event"
    ></component>
    <template #footer>
      <div class="dp-flex wrap justify-content__space-around">
        <a
          v-if="isSetupComponent || isEnterComponent"
          @click="setCurrentComponent(COMPONENTS.MFA_CHOICE)"
          @keypress="setCurrentComponent(COMPONENTS.MFA_CHOICE)"
          tabindex="0"
        >{{ t("sso.pages.LoginStages.switchMethod.linkLabel") }}</a>
        <a
          v-if="(isSetupComponent || isMFAChoiceComponent) && hasNoMFA"
          @click="skipSetup"
          @keypress="skipSetup"
          tabindex="0"
        >{{ t("sso.pages.LoginStages.skipSetupThisTime.linkLabel") }}</a>
        <a
          v-if="isMFAChoiceComponent && hasAtLeastOneMFA"
          @click="setCurrentComponent(COMPONENTS.EMAIL_FORM)"
          @keypress="setCurrentComponent(COMPONENTS.EMAIL_FORM)"
          tabindex="0"
        >{{ t("sso.pages.LoginStages.goBackToLogin.linkLabel") }}</a>
        <a
          v-if="isEmailOTPComponent"
          @click="resetLoginData"
          @keypress="resetLoginData"
          tabindex="0"
        >{{ t("sso.pages.LoginStages.startOver.linkLabel") }}</a>
      </div>
    </template>
  </PageContainer>
</template>

<i18n>
{
  "ar-AE": {
    "skipSetupThisTime": {
      "linkLabel": "يتخطى"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح",
          "message": "نجح تسجيل الخروج"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "رمز المصادقة غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "العودة إلى صفحة الدخول"
    },
    "switchMethod": {
      "linkLabel": "الرجوع إلى النظرة العامة"
    },
    "startOver": {
      "linkLabel": "العودة إلى صفحة الدخول"
    }
  },
  "ar-DZ": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "رمز التوثيق غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "الرجوع إلى صفحة الدخول"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "الرجوع إلى صفحة الدخول"
    }
  },
  "ar-EG": {
    "skipSetupThisTime": {
      "linkLabel": "يتخطى"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح",
          "message": "نجح تسجيل الخروج"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "الرمز التوثيقي غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "العودة إلى صفحة الدخول"
    },
    "switchMethod": {
      "linkLabel": "الرجوع إلى النظرة العامة"
    },
    "startOver": {
      "linkLabel": "العودة إلى صفحة الدخول"
    }
  },
  "ar-LB": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "الرمز التوثيقي غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "العودة إلى صفحة الدخول"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "العودة إلى صفحة الدخول"
    }
  },
  "ar-MA": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "كود التوثيق غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "العودة إلى صفحة الدخول"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "العودة إلى صفحة الدخول"
    }
  },
  "ar-SA": {
    "skipSetupThisTime": {
      "linkLabel": "يتخطى"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح",
          "message": "نجح تسجيل الخروج"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "رمز التحقق غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "عودة إلى صفحة تسجيل الدخول"
    },
    "switchMethod": {
      "linkLabel": "الرجوع إلى النظرة العامة"
    },
    "startOver": {
      "linkLabel": "عودة إلى صفحة تسجيل الدخول"
    }
  },
  "ar-SY": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "كود التوثيق غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "العودة إلى صفحة الدخول"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "العودة إلى صفحة الدخول"
    }
  },
  "ar-TN": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "كود التوثيق غير صالح",
          "title": "خطأ"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "العودة إلى صفحة الدخول"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "العودة إلى صفحة الدخول"
    }
  },
  "bg-BG": {
    "skipSetupThisTime": {
      "linkLabel": "Пропускане"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Успешно действие",
          "message": "Излизането беше успешно"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Кодът за удостоверяване е невалиден",
          "title": "грешка"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Обратно към страницата за вход"
    },
    "switchMethod": {
      "linkLabel": "Назад към общ преглед"
    },
    "startOver": {
      "linkLabel": "Обратно към страницата за вход"
    }
  },
  "bs-BA": {
    "skipSetupThisTime": {
      "linkLabel": "Preskoči"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Uspješno obavljeno",
          "message": "Odjava je bila uspješna"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Kod za autentifikaciju je nevažeći",
          "title": "Greška"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Nazad na stranicu za prijavu"
    },
    "switchMethod": {
      "linkLabel": "Povratak na pregled"
    },
    "startOver": {
      "linkLabel": "Nazad na stranicu za prijavu"
    }
  },
  "cs-CZ": {
    "skipSetupThisTime": {
      "linkLabel": "Přeskočit"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Úspěšná akce",
          "message": "Odhlášení bylo úspěšné"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Ověřovací kód je neplatný",
          "title": "Chyba"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Zpět na přihlašovací stránku"
    },
    "switchMethod": {
      "linkLabel": "Zpět na přehled"
    },
    "startOver": {
      "linkLabel": "Zpět na přihlašovací stránku"
    }
  },
  "da-DK": {
    "skipSetupThisTime": {
      "linkLabel": "spring over"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Vellykket handling",
          "message": "Det lykkedes at logge ud"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Godkendelseskoden er ugyldig",
          "title": "Fejl"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Tilbage til loginsiden"
    },
    "switchMethod": {
      "linkLabel": "Tilbage til oversigt"
    },
    "startOver": {
      "linkLabel": "Tilbage til loginsiden"
    }
  },
  "de-DE": {
    "skipSetupThisTime": {
      "linkLabel": "Überspringen"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Die Abmeldung war erfolgreich"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Zurück zur Anmeldeseite"
    },
    "switchMethod": {
      "linkLabel": "Zurück zur Übersicht"
    },
    "startOver": {
      "linkLabel": "Zurück zur Anmeldeseite"
    }
  },
  "de-AT": {
    "skipSetupThisTime": {
      "linkLabel": "Überspringen"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Die Abmeldung war erfolgreich"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Zurück zur Anmeldeseite"
    },
    "switchMethod": {
      "linkLabel": "Zurück zur Übersicht"
    },
    "startOver": {
      "linkLabel": "Zurück zur Anmeldeseite"
    }
  },
  "de-CH": {
    "skipSetupThisTime": {
      "linkLabel": "Überspringen"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Die Abmeldung war erfolgreich"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Zurück zur Anmeldeseite"
    },
    "switchMethod": {
      "linkLabel": "Zurück zur Übersicht"
    },
    "startOver": {
      "linkLabel": "Zurück zur Anmeldeseite"
    }
  },
  "de-LU": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Zurück zur Anmeldeseite"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "Zurück zur Anmeldeseite"
    }
  },
  "el-GR": {
    "skipSetupThisTime": {
      "linkLabel": "Παράλειψη"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Επιτυχημένη ενέργεια",
          "message": "Η αποσύνδεση ήταν επιτυχής"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Ο κωδικός επαλήθευσης είναι άκυρος",
          "title": "Σφάλμα"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Επιστροφή στη σελίδα σύνδεσης"
    },
    "switchMethod": {
      "linkLabel": "Επιστροφή στην επισκόπηση"
    },
    "startOver": {
      "linkLabel": "Επιστροφή στη σελίδα σύνδεσης"
    }
  },
  "el-CY": {
    "skipSetupThisTime": {
      "linkLabel": "Παράλειψη"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Επιτυχημένη ενέργεια",
          "message": "Η αποσύνδεση ήταν επιτυχής"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Ο κωδικός επαλήθευσης είναι άκυρος",
          "title": "Σφάλμα"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Επιστροφή στη σελίδα σύνδεσης"
    },
    "switchMethod": {
      "linkLabel": "Επιστροφή στην επισκόπηση"
    },
    "startOver": {
      "linkLabel": "Επιστροφή στη σελίδα σύνδεσης"
    }
  },
  "en-GB": {
    "skipSetupThisTime": {
      "linkLabel": "Skip"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Successful Action",
          "message": "Logging out was successful"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "The authentication code is invalid.",
          "title": "Error"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Back to Login Page"
    },
    "switchMethod": {
      "linkLabel": "Back to overview"
    },
    "startOver": {
      "linkLabel": "Back to Login Page"
    }
  },
  "en-IE": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "Successful Action"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "The authentication code is invalid.",
          "title": "Error"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Back to Login Page"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "Back to Login Page"
    }
  },
  "en-US": {
    "skipSetupThisTime": {
      "linkLabel": "Skip"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Successful Action",
          "message": "Logging out was successful"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "The authentication code is invalid.",
          "title": "Error"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Back to Login Page"
    },
    "switchMethod": {
      "linkLabel": "Back to overview"
    },
    "startOver": {
      "linkLabel": "Back to Login Page"
    }
  },
  "es-ES": {
    "skipSetupThisTime": {
      "linkLabel": "Saltar"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Listo",
          "message": "Se ha cerrado la sesión correctamente"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "El código de autenticación es inválido",
          "title": "Error"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Volver a la página de inicio de sesión"
    },
    "switchMethod": {
      "linkLabel": "Atrás"
    },
    "startOver": {
      "linkLabel": "Volver a la página de inicio de sesión"
    }
  },
  "et-EE": {
    "skipSetupThisTime": {
      "linkLabel": "Jäta vahele"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Edukas tegevus",
          "message": "Väljalogimine õnnestus"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Autentimiskood on kehtetu",
          "title": "Viga"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Tagasi sisselogimise lehele"
    },
    "switchMethod": {
      "linkLabel": "Tagasi ülevaate juurde"
    },
    "startOver": {
      "linkLabel": "Tagasi sisselogimise lehele"
    }
  },
  "fi-FI": {
    "skipSetupThisTime": {
      "linkLabel": "Ohita"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Onnistunut toiminta",
          "message": "Uloskirjautuminen onnistui"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Todennuskoodi on virheellinen",
          "title": "Virhe"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Takaisin kirjautumissivulle"
    },
    "switchMethod": {
      "linkLabel": "Takaisin yleiskatsaukseen"
    },
    "startOver": {
      "linkLabel": "Takaisin kirjautumissivulle"
    }
  },
  "fr-BE": {
    "skipSetupThisTime": {
      "linkLabel": "Sauter"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie",
          "message": "La déconnexion a réussi"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Retour à la page de connexion"
    },
    "switchMethod": {
      "linkLabel": "Retour à la page d'accueil"
    },
    "startOver": {
      "linkLabel": "Retour à la page de connexion"
    }
  },
  "fr-CH": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Retour à la page de connexion"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "Retour à la page de connexion"
    }
  },
  "fr-FR": {
    "skipSetupThisTime": {
      "linkLabel": "Sauter"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie",
          "message": "La déconnexion a réussi"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Retour à la page de connexion"
    },
    "switchMethod": {
      "linkLabel": "Retour à la page d'accueil"
    },
    "startOver": {
      "linkLabel": "Retour à la page de connexion"
    }
  },
  "fr-LU": {
    "skipSetupThisTime": {
      "linkLabel": "Sauter"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie",
          "message": "La déconnexion a réussi"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Retour à la page de connexion"
    },
    "switchMethod": {
      "linkLabel": "Retour à la page d'accueil"
    },
    "startOver": {
      "linkLabel": "Retour à la page de connexion"
    }
  },
  "ga-IE": {
    "skipSetupThisTime": {
      "linkLabel": "Scipeáil"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Gníomh rathúil",
          "message": "D'éirigh leis an logáil amach"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Tá an cód fíordheimhnithe neamhbhailí",
          "title": "Earráid"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Ar ais go dtí an leathanach logáil isteach"
    },
    "switchMethod": {
      "linkLabel": "Ar ais go forbhreathnú"
    },
    "startOver": {
      "linkLabel": "Ar ais go dtí an leathanach logáil isteach"
    }
  },
  "he-IL": {
    "skipSetupThisTime": {
      "linkLabel": "לדלג"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "פעולה מוצלחת",
          "message": "ההתנתקות הצליחה"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "קוד האימות אינו חוקי",
          "title": "שְׁגִיאָה"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "חזרה לדף הכניסה"
    },
    "switchMethod": {
      "linkLabel": "חזרה לסקירה כללית"
    },
    "startOver": {
      "linkLabel": "חזרה לדף הכניסה"
    }
  },
  "hi-IN": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "सफल कार्रवाई"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "प्रमाणीकरण कोड अमान्य है",
          "title": "त्रुटि"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "लॉगिन पेज पर वापस जाएं"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "लॉगिन पेज पर वापस जाएं"
    }
  },
  "hr-HR": {
    "skipSetupThisTime": {
      "linkLabel": "Preskočiti"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Uspješna akcija",
          "message": "Odjava je bila uspješna"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Kod za autentifikaciju je nevažeći",
          "title": "Pogreška"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Vratite se na stranicu za prijavu"
    },
    "switchMethod": {
      "linkLabel": "Povratak na pregled"
    },
    "startOver": {
      "linkLabel": "Vratite se na stranicu za prijavu"
    }
  },
  "hu-HU": {
    "skipSetupThisTime": {
      "linkLabel": "Kihagy"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Sikeres művelet",
          "message": "A kijelentkezés sikeres volt"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Az hitelesítési kód érvénytelen",
          "title": "Hiba"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Vissza a bejelentkezési oldalra"
    },
    "switchMethod": {
      "linkLabel": "Vissza az áttekintéshez"
    },
    "startOver": {
      "linkLabel": "Vissza a bejelentkezési oldalra"
    }
  },
  "it-CH": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "Azione di successo"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Il codice di autenticazione non è valido",
          "title": "Errore"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Torna alla pagina di accesso"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "Torna alla pagina di accesso"
    }
  },
  "it-IT": {
    "skipSetupThisTime": {
      "linkLabel": "Salta"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Azione riuscita",
          "message": "La disconnessione ha avuto successo"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Il codice di autenticazione non è valido",
          "title": "Errore"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Torna alla pagina di login"
    },
    "switchMethod": {
      "linkLabel": "Torna alla panoramica"
    },
    "startOver": {
      "linkLabel": "Torna alla pagina di login"
    }
  },
  "ja-JP": {
    "skipSetupThisTime": {
      "linkLabel": "スキップ"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "成功したアクション",
          "message": "ログアウト成功"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "認証コードが無効です",
          "title": "エラー"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "ログインページに戻ります"
    },
    "switchMethod": {
      "linkLabel": "概要に戻る"
    },
    "startOver": {
      "linkLabel": "ログインページに戻ります"
    }
  },
  "ko-KR": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "성공적인 액션"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "인증 코드가 유효하지 않습니다",
          "title": "오류"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "로그인 페이지로 돌아가기"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "로그인 페이지로 돌아가기"
    }
  },
  "lt-LT": {
    "skipSetupThisTime": {
      "linkLabel": "Praleisti"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Sėkmingas veiksmas",
          "message": "Atsijungimas buvo sėkmingas"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Autentifikacijos kodas netinkamas",
          "title": "Klaida"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Grįžti į prisijungimo puslapį"
    },
    "switchMethod": {
      "linkLabel": "Grįžti į apžvalgą"
    },
    "startOver": {
      "linkLabel": "Grįžti į prisijungimo puslapį"
    }
  },
  "lv-LV": {
    "skipSetupThisTime": {
      "linkLabel": "Izlaist"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Veiksmīga darbība",
          "message": "Atteikšanās sekmīga"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Autentifikācijas kods ir nederīgs",
          "title": "Kļūda"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Atgriezties uz pieteikšanās lapu"
    },
    "switchMethod": {
      "linkLabel": "Atgriezties uz kopsavilkumu"
    },
    "startOver": {
      "linkLabel": "Atgriezties uz pieteikšanās lapu"
    }
  },
  "mt-MT": {
    "skipSetupThisTime": {
      "linkLabel": "Aqbeż"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Azzjoni ta' suċċess",
          "message": "Id-dereġistrazzjoni kienet suċċess"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Il-kodiċi ta' awtentiċità mhux validu",
          "title": "Żball"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Lura għall-Paġna tal-Login"
    },
    "switchMethod": {
      "linkLabel": "Lura għall-ħarsa ġenerali"
    },
    "startOver": {
      "linkLabel": "Lura għall-Paġna tal-Login"
    }
  },
  "nb-NO": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "Vellykket handling"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Autentiseringskoden er ugyldig",
          "title": "Feil"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Tilbake til innloggingssiden"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "Tilbake til innloggingssiden"
    }
  },
  "nl-BE": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "Geslaagde actie"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "De verificatiecode is ongeldig",
          "title": "Fout"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Terug naar de inlogpagina"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "Terug naar de inlogpagina"
    }
  },
  "nl-NL": {
    "skipSetupThisTime": {
      "linkLabel": "Overslaan"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Geslaagde actie",
          "message": "Uitloggen is gelukt"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "De verificatiecode is ongeldig",
          "title": "Fout"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Terug naar inlogpagina"
    },
    "switchMethod": {
      "linkLabel": "Terug naar overzicht"
    },
    "startOver": {
      "linkLabel": "Terug naar inlogpagina"
    }
  },
  "pl-PL": {
    "skipSetupThisTime": {
      "linkLabel": "Pomiń"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Czynność zakończona powodzeniem",
          "message": "Wylogowanie powiodło się"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Kod uwierzytelniania jest nieprawidłowy",
          "title": "Błąd"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Powrót do strony logowania"
    },
    "switchMethod": {
      "linkLabel": "Powrót do przeglądu"
    },
    "startOver": {
      "linkLabel": "Powrót do strony logowania"
    }
  },
  "pt-PT": {
    "skipSetupThisTime": {
      "linkLabel": "Ignorar"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Ação bem-sucedida",
          "message": "Logout bem-sucedido"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "O código de autenticação é inválido",
          "title": "Erro"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Voltar à página de início de sessão"
    },
    "switchMethod": {
      "linkLabel": "Voltar ao menu principal"
    },
    "startOver": {
      "linkLabel": "Voltar à página de início de sessão"
    }
  },
  "ro-RO": {
    "skipSetupThisTime": {
      "linkLabel": "Ignorare"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Acțiune reușită",
          "message": "Deconectarea a avut succes"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Codul de autentificare este invalid",
          "title": "Eroare"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Înapoi la pagina de conectare"
    },
    "switchMethod": {
      "linkLabel": "Înapoi la prezentarea generală"
    },
    "startOver": {
      "linkLabel": "Înapoi la pagina de conectare"
    }
  },
  "ru-RU": {
    "skipSetupThisTime": {
      "linkLabel": "Пропустить"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Успешное действие",
          "message": "Выход из системы прошел успешно"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Код аутентификации недействителен",
          "title": "Ошибка"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Назад на страницу входа"
    },
    "switchMethod": {
      "linkLabel": "Вернуться к общим сведениям"
    },
    "startOver": {
      "linkLabel": "Назад на страницу входа"
    }
  },
  "sk-SK": {
    "skipSetupThisTime": {
      "linkLabel": "Preskočiť"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Úspešná akcia",
          "message": "Odhlásenie bolo úspešné"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Autentifikačný kód je neplatný",
          "title": "Chyba"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Späť na prihlasovaciu stránku"
    },
    "switchMethod": {
      "linkLabel": "Späť na prehľad"
    },
    "startOver": {
      "linkLabel": "Späť na prihlasovaciu stránku"
    }
  },
  "sl-SI": {
    "skipSetupThisTime": {
      "linkLabel": "Preskoči"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Uspešno opravljeno",
          "message": "Odjava je uspela"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Koda za overitev je neveljavna",
          "title": "Napaka"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Nazaj na prijavo"
    },
    "switchMethod": {
      "linkLabel": "Nazaj na pregled"
    },
    "startOver": {
      "linkLabel": "Nazaj na prijavo"
    }
  },
  "sv-SE": {
    "skipSetupThisTime": {
      "linkLabel": "Hoppa över"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Åtgärd utförd",
          "message": "Utloggningen lyckades"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Autentiseringskoden är ogiltig",
          "title": "Fel"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Återvänd till inloggningssidan"
    },
    "switchMethod": {
      "linkLabel": "Tillbaka till översikt"
    },
    "startOver": {
      "linkLabel": "Återvänd till inloggningssidan"
    }
  },
  "th-TH": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "การดำเนินการที่ประสบความสำเร็จ"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "รหัสการตรวจสอบไม่ถูกต้อง",
          "title": "ความผิดพลาด"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "กลับไปยังหน้าล็อกอิน"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "กลับไปยังหน้าล็อกอิน"
    }
  },
  "tr-CY": {
    "skipSetupThisTime": {
      "linkLabel": "Atla"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Başarılı eylem",
          "message": "Çıkış başarılı oldu"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Kimlik doğrulama kodu geçersiz",
          "title": "Hata"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Oturum açma sayfasına dön"
    },
    "switchMethod": {
      "linkLabel": "Genel bakışa dön"
    },
    "startOver": {
      "linkLabel": "Oturum açma sayfasına dön"
    }
  },
  "tr-TR": {
    "skipSetupThisTime": {
      "linkLabel": "Atla"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Başarılı eylem",
          "message": "Çıkış başarılı oldu"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Doğrulama kodu geçersiz",
          "title": "Hata"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Oturum açma sayfasına dön"
    },
    "switchMethod": {
      "linkLabel": "Genel bakışa dön"
    },
    "startOver": {
      "linkLabel": "Oturum açma sayfasına dön"
    }
  },
  "uk-UA": {
    "skipSetupThisTime": {
      "linkLabel": "Überspringen"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "Успішна дія",
          "message": "Вихід успішно виконано"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Код автентифікації недійсний",
          "title": "Помилка"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Повернутися до сторінки входу"
    },
    "switchMethod": {
      "linkLabel": "Zurück zur Übersicht"
    },
    "startOver": {
      "linkLabel": "Повернутися до сторінки входу"
    }
  },
  "vi-VN": {
    "skipSetupThisTime": {},
    "notification": {
      "logout": {
        "success": {
          "title": "Hành động thành công"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "Mã xác thực không hợp lệ",
          "title": "Lỗi"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "Quay lại trang đăng nhập"
    },
    "switchMethod": {},
    "startOver": {
      "linkLabel": "Quay lại trang đăng nhập"
    }
  },
  "zh-CN": {
    "skipSetupThisTime": {
      "linkLabel": "跳过"
    },
    "notification": {
      "logout": {
        "success": {
          "title": "操作成功",
          "message": "注销成功"
        }
      },
      "authenticate": {
        "asyncError": {
          "message": "认证代码无效",
          "title": "错误"
        }
      }
    },
    "goBackToLogin": {
      "linkLabel": "返回登录页面"
    },
    "switchMethod": {
      "linkLabel": "返回概览"
    },
    "startOver": {
      "linkLabel": "返回登录页面"
    }
  }
}
</i18n>

<style lang="scss"></style>
